import styled, { css } from 'styled-components/macro'

import Divider from 'assets/divider.png'
import BackgroundImage from 'assets/backgrund.png'
import Mask from 'assets/mask.png'
import HintMask from 'assets/mask-timeline-hint.png'
import TimelineBg from 'assets/timeline.png'
import TimelineVerticalBg from 'assets/timeline-vertical.png'
import TimelineDot from 'assets/timeline-dot.png'
import { theme } from 'styles/theme'
import { mq } from 'styles/mq'
import { motion } from 'framer-motion'
import { BubblePosition } from './bubble/styled'

export const Section = styled.section`
  position: relative;
  margin-bottom: -30rem;

  ${mq.medium} {
    height: 600vw;
  }

  &::before {
    content: '';
    display: block;
    position: sticky;
    top: -10rem;
    width: 100%;
    height: 43.4rem;
    max-height: calc(100% + 8rem);
    margin-block: -10rem -35rem;
    pointer-events: none;
    background: url('${Divider}') 0 0 repeat-x;
    z-index: 10;
  }
`

export const Title = styled.h2`
  position: sticky;
  right: 0;
  left: 0;
  width: 100vw;
  margin: 0;
  padding-block: 10rem 0.2em;
  text-align: center;
  font: 300 clamp(3rem, 10vw, 8rem) / 1.2 var(--font-headings);
  mask: url('${Mask}') center 20% repeat;

  ${mq.medium} {
    padding-block-start: 15rem;
  }
`

export const Hint = styled.p`
  position: sticky;
  right: 0;
  left: 0;
  width: 100vw;
  margin-block: 0;
  padding-inline: 2em;
  text-align: center;
  mask: url('${HintMask}') center center repeat;

  &::before {
    content: '';
    display: inline-block;
    vertical-align: middle;
    width: 1.5em;
    aspect-ratio: 1;
    margin-inline-end: 0.3em;
    mask: url("data:image/svg+xml,%3Csvg viewBox='0 0 27 27' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M22.5 23.625H4.5C3.87868 23.625 3.375 23.1213 3.375 22.5V4.5C3.375 3.87868 3.87868 3.375 4.5 3.375H22.5C23.1213 3.375 23.625 3.87868 23.625 4.5V22.5C23.625 23.1213 23.1213 23.625 22.5 23.625ZM11.25 12.375V14.625H12.375V19.125H15.75V16.875H14.625V12.375H11.25ZM12.375 7.875V10.125H14.625V7.875H12.375Z' fill='black'/%3E%3C/svg%3E%0A");
    background-color: ${theme.colors.accent.primary};
  }
`

export const ListContainer = styled.div`
  --dot-size: 4rem;
  --bubble-width: min(40rem, 70vw, calc(100vw - 2 * var(--container-spacing)));
  --item-spacing: 10rem;

  display: flex;
  z-index: 0;

  ${mq.smallOnly} {
    display: block;
    padding-top: 7rem;
  }

  > * {
    flex-shrink: 0;
  }

  ${mq.medium} {
    &::before {
      content: '';
      display: block;
      width: max(
        calc((100vw - var(--container-width)) / 2),
        var(--container-spacing)
      );
      flex-shrink: 0;
    }
  }
`

export const ScrollingContent = styled(motion.div)`
  position: relative;
  z-index: 1;

  ${mq.medium} {
    position: sticky;
    top: 0;
    overflow: hidden;
    background: url('${BackgroundImage}') center top repeat;
  }
`

export const ScrollInner = styled(motion.div)`
  position: relative;
  width: 100%;

  ${mq.medium} {
    width: min-content;
  }

  &::before {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: url('${BackgroundImage}') center top repeat;
    z-index: -1;
  }

  &::after {
    content: '';
    display: block;
    height: 43.4rem;
    max-height: calc(100% + 8rem);
    pointer-events: none;
    left: 0;
    width: 100%;
    background: url('${Divider}') 0 0 repeat-x,
      linear-gradient(to bottom, transparent 15%, white 15%);
  }
`

export const TimelineList = styled.ul`
  margin: 0 0 -5rem;
  padding: 0;
  list-style: none;
  mix-blend-mode: multiply;
  font-size: 0;
  white-space: nowrap;

  ${mq.smallOnly} {
    padding-inline: var(--container-spacing);
    background: url('${TimelineVerticalBg}') calc(100% - 1rem) 0 / auto 30rem
      repeat-y;
  }

  ${mq.medium} {
    display: grid;
    grid-auto-flow: column;
    grid-template-rows: auto min-content auto;
    width: min-content;
    min-height: calc(100vh - 15rem);
    min-height: calc(100svh - 15rem);
    padding-top: 10rem;
  }
`

export const Item = styled.li.attrs({
  role: 'button',
  tabIndex: 0,
})<BubblePosition>`
  position: relative;
  min-width: var(--bubble-width);
  width: min-content;
  padding-inline-end: var(--item-spacing);
  cursor: pointer;
  white-space: normal;
  font-size: 1.4rem;

  &::after {
    content: '';
    display: block;
    width: var(--dot-size);
    height: var(--dot-size);
    margin-inline: calc(-0.2 * var(--dot-size));
    grid-area: dot;
    background: url('${TimelineDot}') center center / contain no-repeat;
  }

  ${mq.smallOnly} {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding-inline: 3em 0;
    padding-block-end: 2em;

    &::after {
      display: none;
    }
  }

  ${mq.medium} {
    display: grid;
    grid-row: 1 / -1;
    grid-template-rows: subgrid;
    grid-template-areas: 'bubble' 'dot' 'text';
    font-size: 1.6rem;

    &::before {
      content: '';
      display: block;
      width: calc(100% + var(--item-spacing));
      height: var(--dot-size);
      grid-area: dot;
      background: url('${TimelineBg}') center center repeat-x;
    }
  }

  &:last-child {
    --item-spacing: max(
      calc((100vw - var(--container-width)) / 2),
      var(--container-spacing)
    );
  }

  ${props =>
    props.$position === 'bottom' &&
    css`
      ${mq.medium} {
        grid-template-areas: 'text' 'dot' 'bubble';
      }

      > *:first-child {
        ${mq.medium} {
          align-self: end;
        }
      }
    `}
`

export const Text = styled.div`
  margin: 0.4em 0;
  grid-area: text;

  ${mq.smallOnly} {
    position: relative;
    width: 100%;
    order: 1;

    > * {
      mask: url('${Mask}') center center repeat;
    }

    &::before {
      content: '';
      display: block;
      position: absolute;
      left: 100%;
      width: var(--dot-size);
      height: var(--dot-size);
      margin: 0.2em 0.8em;
      background: url('${TimelineDot}') center center / contain no-repeat;
    }
  }

  ${mq.medium} {
    mask: url('${Mask}') center center repeat;
  }
`

export const Date = styled.h3`
  display: block;
  margin: 0;
  font-weight: 500;
  font-size: 2.4rem;
  color: ${theme.colors.accent.primary};

  ${mq.medium} {
    font-size: 3rem;
  }
`

export const Description = styled.p`
  margin: 0 0 0.3em;
  font-size: 2rem;

  a {
    display: inline-block;
    position: relative;

    &:hover {
      color: ${theme.colors.accent.primary};
    }
  }
`

export const Links = styled.p`
  margin: -0.3em 0 0.3em;
`

export const Link = styled.a`
  display: inline-block;
  white-space: nowrap;
  color: ${theme.colors.accent.primary};
  font-size: 2.3rem;
`
